import { useState, useEffect, Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import EmployeeDetailsTab from './EmployeeDetailsTab';
import EmployeePermissionsTab from './EmployeePermissionsTab';
import { usePermissions } from '../../../contexts/PermissionContext';
import { PERMISSIONS } from '../../../constants/permissionMappings';

/**
 * EmployeeSlideout - Production-quality right-side slideout panel
 *
 * Design inspired by: Flowbite, Tailwind UI, and Headless UI best practices
 *
 * Features:
 * - Smooth asymmetric animations (ease-out enter, ease-in leave)
 * - Professional backdrop with blur effect
 * - Deep shadows for visual depth
 * - Optimal width for form content (max-w-md instead of max-w-2xl)
 * - Enhanced close button with hover states
 * - Clean tab navigation with active indicators
 * - Unsaved changes protection
 *
 * @param {boolean} isOpen - Controls slideout visibility
 * @param {function} onClose - Called when slideout closes
 * @param {object} employee - Employee data to display/edit
 * @param {function} onEmployeeUpdated - Called after successful update
 */
function EmployeeSlideout({ isOpen, onClose, employee, onEmployeeUpdated }) {
  const { hasPermission } = usePermissions();
  const [activeTab, setActiveTab] = useState('details'); // 'details' or 'permissions'
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showConfirmClose, setShowConfirmClose] = useState(false);

  // Check if user can edit permissions
  const canEditPermissions = hasPermission(PERMISSIONS.employees.edit_all);

  // Reset state only when slideout opens (not when it closes)
  useEffect(() => {
    if (isOpen && employee) {
      setActiveTab('details');
      setHasUnsavedChanges(false);
      setShowConfirmClose(false);
    }
  }, [isOpen, employee]);

  // Handle close with unsaved changes check
  const handleClose = () => {
    if (hasUnsavedChanges) {
      setShowConfirmClose(true);
    } else {
      onClose();
    }
  };

  // Force close without saving
  const handleForceClose = () => {
    setShowConfirmClose(false);
    setHasUnsavedChanges(false);
    onClose();
  };

  // Get status badge styling
  const getStatusBadge = (status) => {
    const statusLower = status?.toLowerCase() || 'inactive';
    const variants = {
      active: 'bg-green-50 text-green-700 border-green-200',
      inactive: 'bg-yellow-50 text-yellow-700 border-yellow-200',
      terminated: 'bg-red-50 text-red-700 border-red-200'
    };
    const dotColors = {
      active: 'bg-green-500',
      inactive: 'bg-yellow-500',
      terminated: 'bg-red-500'
    };
    return {
      badge: variants[statusLower] || variants.inactive,
      dot: dotColors[statusLower] || dotColors.inactive,
      label: status?.charAt(0).toUpperCase() + status?.slice(1) || 'Inactive'
    };
  };

  if (!employee) return null;

  const statusBadge = getStatusBadge(employee.employmentStatus);

  return (
    <>
      <Transition.Root show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={handleClose}>
          {/* Enhanced Backdrop with Blur Effect */}
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-500"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-400"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-hidden">
            <div className="absolute inset-0 overflow-hidden">
              <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10 sm:pl-16">
                {/* Slideout Panel - Enhanced Animations */}
                <Transition.Child
                  as={Fragment}
                  enter="transform transition ease-out duration-500"
                  enterFrom="translate-x-full"
                  enterTo="translate-x-0"
                  leave="transform transition ease-in duration-400"
                  leaveFrom="translate-x-0"
                  leaveTo="translate-x-full"
                >
                  <Dialog.Panel className="pointer-events-auto w-screen max-w-md">
                    <div className="flex h-full flex-col bg-white shadow-2xl">

                      {/* Header - Enhanced Spacing */}
                      <div className="bg-white px-6 py-6 border-b border-gray-200">
                        <div className="flex items-start justify-between">
                          <Dialog.Title className="text-lg font-semibold leading-6 text-gray-900">
                            {activeTab === 'details' ? 'Employee Details' : 'Permissions'}
                          </Dialog.Title>
                          {/* Enhanced Close Button */}
                          <div className="ml-3 flex h-7 items-center">
                            <button
                              type="button"
                              className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors duration-200"
                              onClick={handleClose}
                            >
                              <span className="sr-only">Close panel</span>
                              <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                            </button>
                          </div>
                        </div>

                        {/* Employee Summary - Enhanced Layout */}
                        <div className="mt-6">
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-3">
                                {/* Avatar with Gradient */}
                                <div className="flex-shrink-0 flex items-center justify-center w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 text-white font-semibold text-sm shadow-md">
                                  {employee.firstName?.[0]}{employee.lastName?.[0]}
                                </div>
                                <div className="min-w-0 flex-1">
                                  <h3 className="text-base font-semibold text-gray-900 truncate">
                                    {employee.firstName} {employee.lastName}
                                  </h3>
                                  <p className="text-sm text-gray-500 truncate">{employee.email}</p>
                                  {employee.jobTitle && (
                                    <p className="text-sm text-gray-500 truncate mt-0.5">{employee.jobTitle}</p>
                                  )}
                                </div>
                              </div>
                            </div>
                            {/* Enhanced Status Badge */}
                            <span className={`flex-shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${statusBadge.badge} shadow-sm`}>
                              <span className={`w-1.5 h-1.5 rounded-full ${statusBadge.dot}`} />
                              {statusBadge.label}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Tabs - Enhanced Styling */}
                      <div className="border-b border-gray-200 bg-gray-50 px-6">
                        <nav className="-mb-px flex gap-8" aria-label="Tabs">
                          <button
                            onClick={() => setActiveTab('details')}
                            className={`
                              relative whitespace-nowrap py-4 px-1 font-medium text-sm transition-colors duration-200
                              ${activeTab === 'details'
                                ? 'text-blue-600'
                                : 'text-gray-500 hover:text-gray-700'
                              }
                            `}
                          >
                            Employee Details
                            {activeTab === 'details' && (
                              <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 rounded-full" />
                            )}
                          </button>
                          {canEditPermissions && (
                          <button
                            onClick={() => setActiveTab('permissions')}
                            className={`
                              relative whitespace-nowrap py-4 px-1 font-medium text-sm transition-colors duration-200
                              ${activeTab === 'permissions'
                                ? 'text-blue-600'
                                : 'text-gray-500 hover:text-gray-700'
                              }
                            `}
                          >
                            Permissions
                            {activeTab === 'permissions' && (
                              <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 rounded-full" />
                            )}
                          </button>
                          )}
                        </nav>
                      </div>

                      {/* Tab Content - Smooth Scrolling */}
                      <div className="flex-1 overflow-y-auto">
                        {activeTab === 'details' ? (
                          <EmployeeDetailsTab
                            employee={employee}
                            onEmployeeUpdated={onEmployeeUpdated}
                            onClose={onClose}
                            onUnsavedChanges={setHasUnsavedChanges}
                          />
                        ) : (
                          <EmployeePermissionsTab
                            employee={employee}
                            onEmployeeUpdated={onEmployeeUpdated}
                            onClose={onClose}
                            onUnsavedChanges={setHasUnsavedChanges}
                          />
                        )}
                      </div>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Confirm Close Dialog - Enhanced Styling */}
      <Transition.Root show={showConfirmClose} as={Fragment}>
        <Dialog as="div" className="relative z-[60]" onClose={() => setShowConfirmClose(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-xl bg-white px-4 pb-4 pt-5 text-left shadow-2xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                  <div>
                    <div className="mt-3 text-center sm:mt-5">
                      <Dialog.Title as="h3" className="text-lg font-semibold leading-6 text-gray-900">
                        Unsaved Changes
                      </Dialog.Title>
                      <div className="mt-2">
                        <p className="text-sm text-gray-500">
                          You have unsaved changes. Are you sure you want to close without saving?
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                    <button
                      type="button"
                      className="inline-flex w-full justify-center rounded-lg bg-white px-3 py-2.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 transition-colors duration-200 sm:col-start-1"
                      onClick={() => setShowConfirmClose(false)}
                    >
                      Keep Editing
                    </button>
                    <button
                      type="button"
                      className="inline-flex w-full justify-center rounded-lg bg-red-600 px-3 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-red-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600 transition-colors duration-200 sm:col-start-2"
                      onClick={handleForceClose}
                    >
                      Discard Changes
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  );
}

export default EmployeeSlideout;
